import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Plus, KeyRound, BookOpen, ShoppingCart, Trash2, Pencil, Save, X, ArrowLeft, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth, can } from "@/lib/auth";

const MARKETPLACES = ["Amazon", "Mercado Livre", "Shopee", "Magalu", "Americanas", "Estante Virtual", "Outro"];

export default function Authors() {
  const { user } = useAuth();
  const [authors, setAuthors] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [editingLogin, setEditingLogin] = useState(false);

  const load = async () => {
    const r = await api.get("/authors");
    setAuthors(r.data || []);
  };
  useEffect(() => { if (can(user, "manage_authors")) load(); }, [user]);

  const filtered = authors.filter(a => !search || a.nome?.toLowerCase().includes(search.toLowerCase()) || a.cpf?.includes(search));
  const active = useMemo(() => authors.find(a => a.client_id === activeId), [authors, activeId]);

  if (user && !can(user, "manage_authors")) return <Navigate to="/" replace />;

  return (
    <Layout title="Autores" subtitle="Logins, livros, vendas em marketplaces e royalties">
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-3 sm:gap-4">
        <Card className={`rounded-sm overflow-hidden flex-col max-h-[calc(100vh-180px)] ${active ? "hidden lg:flex" : "flex"}`}>
          <div className="p-3 border-b border-border">
            <Input data-testid="authors-search" placeholder="Buscar autor (nome ou CPF)…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map(a => (
              <button
                key={a.client_id}
                data-testid={`author-${a.client_id}`}
                onClick={() => setActiveId(a.client_id)}
                className={`w-full text-left p-3 border-b border-border/50 hover:bg-secondary/40 flex gap-2 items-start ${activeId === a.client_id ? "bg-secondary/60" : ""}`}
              >
                <Avatar className="h-9 w-9"><AvatarFallback>{(a.nome || "?")[0]?.toUpperCase()}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <div className="text-sm font-semibold truncate">{a.nome}</div>
                    {a.user ? (
                      <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">login</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px] text-muted-foreground">sem login</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">{a.cpf || "—"}</div>
                  <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`author-books-${a.client_id}`}>
                    {(a.books || []).length === 0 ? (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">sem livros</span>
                    ) : (
                      <>
                        <BookOpen className="w-3 h-3 text-[hsl(var(--primary))]" />
                        <span className="text-[10px] font-semibold text-[hsl(var(--primary))]">{a.books_count}</span>
                        <span className="text-[10px] text-muted-foreground truncate">
                          {(a.books || []).slice(0, 2).map(b => b.titulo).join(", ")}
                          {(a.books_count || 0) > 2 && ` +${a.books_count - 2}`}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="p-6 text-xs text-muted-foreground text-center">Nenhum cliente encontrado.</div>}
          </div>
        </Card>

        <div className={`${active ? "block" : "hidden lg:block"}`}>
          {!active && (
            <Card className="rounded-sm p-10 text-center text-sm text-muted-foreground">
              Selecione um autor à esquerda para gerenciar livros, vendas e royalties.
            </Card>
          )}
          {active && (
            <div className="space-y-3">
              <Button
                data-testid="author-back-btn"
                size="sm"
                variant="ghost"
                className="lg:hidden -ml-2"
                onClick={() => setActiveId(null)}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />Voltar para a lista
              </Button>
              <AuthorPanel
                author={active}
                onReload={load}
                onCreateLogin={() => { setEditingLogin(false); setShowLogin(true); }}
                onEditLogin={() => { setEditingLogin(true); setShowLogin(true); }}
              />
            </div>
          )}
        </div>
      </div>

      <LoginDialog
        open={showLogin}
        onClose={() => setShowLogin(false)}
        author={active}
        editing={editingLogin}
        onSaved={() => { setShowLogin(false); load(); }}
      />
    </Layout>
  );
}

function AuthorPanel({ author, onReload, onCreateLogin, onEditLogin }) {
  const [summary, setSummary] = useState(null);
  const reload = async () => {
    const r = await api.get("/marketplace/summary", { params: { client_id: author.client_id } });
    setSummary(r.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [author.client_id]);

  return (
    <div className="space-y-4">
      <Card className="rounded-sm p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Cliente</div>
            <div className="text-xl font-semibold mt-1">{author.nome}</div>
            <div className="text-xs text-muted-foreground">{author.cpf} · {author.email || "—"}</div>
            {author.user ? (
              <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-sm bg-secondary/60 border border-border">
                <KeyRound className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                Login: <b>{author.user.username}</b>
                <Badge variant="outline" className={`text-[9px] ${author.user.active ? "text-emerald-700 border-emerald-200" : "text-red-700 border-red-200"}`}>
                  {author.user.active ? "Ativo" : "Inativo"}
                </Badge>
              </div>
            ) : (
              <div className="mt-3 text-xs text-amber-700">Este cliente ainda não tem login de autor.</div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {author.user
              ? <Button data-testid="author-edit-login" size="sm" variant="outline" onClick={onEditLogin}><Pencil className="w-3.5 h-3.5 mr-1" />Editar login</Button>
              : <Button data-testid="author-create-login" size="sm" onClick={onCreateLogin}><KeyRound className="w-3.5 h-3.5 mr-1" />Criar login</Button>
            }
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Kpi label="Vendas totais" value={fmtBRL(summary?.totals?.vendas || 0)} />
        <Kpi label="Royalties (15%)" value={fmtBRL(summary?.totals?.royalties || 0)} />
        <Kpi label="Disponível p/ solicitar" value={fmtBRL(summary?.totals?.royalties_disponivel || 0)} accent />
        <Kpi label="Solicitados" value={fmtBRL(summary?.totals?.royalties_solicitados || 0)} />
      </div>

      <Tabs defaultValue="livros">
        <TabsList>
          <TabsTrigger value="livros" data-testid="author-tab-livros"><BookOpen className="w-3.5 h-3.5 mr-1" />Livros</TabsTrigger>
          <TabsTrigger value="vendas" data-testid="author-tab-vendas"><ShoppingCart className="w-3.5 h-3.5 mr-1" />Vendas em Marketplaces</TabsTrigger>
          <TabsTrigger value="royalties" data-testid="author-tab-royalties">Royalties</TabsTrigger>
        </TabsList>
        <TabsContent value="livros"><BooksSection author={author} summary={summary} onChange={reload} /></TabsContent>
        <TabsContent value="vendas"><SalesSection author={author} summary={summary} onChange={() => { reload(); onReload?.(); }} /></TabsContent>
        <TabsContent value="royalties"><RoyaltiesSection summary={summary} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <Card className={`p-4 rounded-sm ${accent ? "border-2 border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.04)]" : ""}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{label}</p>
      <p className={`kpi-num text-2xl mt-2 ${accent ? "text-[hsl(var(--primary))]" : ""}`}>{value}</p>
    </Card>
  );
}

function BooksSection({ author, summary, onChange }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ titulo: "", isbn: "" });
  const [editingLinks, setEditingLinks] = useState(null);
  const books = summary?.books || [];

  const save = async () => {
    if (!form.titulo.trim()) return toast.error("Informe o título");
    try {
      await api.post("/marketplace/books", { client_id: author.client_id, titulo: form.titulo, isbn: form.isbn });
      toast.success("Livro cadastrado");
      setShowNew(false); setForm({ titulo: "", isbn: "" }); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const del = async (b) => {
    if (!window.confirm(`Excluir "${b.titulo}"?`)) return;
    try { await api.delete(`/marketplace/books/${b.id}`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <Card className="rounded-sm overflow-hidden">
      <div className="p-3 border-b border-border flex justify-between items-center">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Livros do autor</div>
        <Button data-testid="book-new-btn" size="sm" onClick={() => setShowNew(true)}><Plus className="w-3.5 h-3.5 mr-1" />Novo livro</Button>
      </div>
      <Table>
        <TableHeader className="bg-secondary/60"><TableRow>
          <TableHead>Título</TableHead><TableHead>ISBN</TableHead>
          <TableHead className="text-right">Vendas</TableHead>
          <TableHead className="text-right">Faturamento</TableHead>
          <TableHead className="text-right">Royalties</TableHead>
          <TableHead className="text-center">Links</TableHead>
          <TableHead className="text-right">Ações</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {books.map(b => (
            <TableRow key={b.id} data-testid={`book-row-${b.id}`}>
              <TableCell className="font-medium">{b.titulo}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{b.isbn || "—"}</TableCell>
              <TableCell className="text-right">{b.vendas}</TableCell>
              <TableCell className="text-right">{fmtBRL(b.valor_vendas)}</TableCell>
              <TableCell className="text-right text-[hsl(var(--primary))] font-semibold">{fmtBRL(b.royalties_total)}</TableCell>
              <TableCell className="text-center">
                <Button size="sm" variant="outline" onClick={() => setEditingLinks(b)} data-testid={`book-links-${b.id}`}>
                  <LinkIcon className="w-3.5 h-3.5 mr-1" />
                  {(b.marketplace_links || []).length}
                </Button>
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => del(b)} data-testid={`book-del-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {books.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Nenhum livro cadastrado.</TableCell></TableRow>}
        </TableBody>
      </Table>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo livro</DialogTitle>
            <DialogDescription>Cadastre um livro do autor. As vendas serão lançadas vinculadas a este livro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Título</Label><Input data-testid="book-titulo" value={form.titulo} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">ISBN (opcional)</Label><Input data-testid="book-isbn" value={form.isbn} onChange={(e) => setForm(f => ({ ...f, isbn: e.target.value }))} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button><Button onClick={save} data-testid="book-save"><Save className="w-3.5 h-3.5 mr-1" />Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <MarketplaceLinksDialog
        book={editingLinks}
        onClose={() => setEditingLinks(null)}
        onSaved={() => { setEditingLinks(null); onChange(); }}
      />
    </Card>
  );
}

function MarketplaceLinksDialog({ book, onClose, onSaved }) {
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (book) setLinks([...(book.marketplace_links || [])]);
  }, [book]);

  if (!book) return null;
  const update = (i, key, val) => setLinks(l => l.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const add = () => setLinks(l => [...l, { nome: "", url: "" }]);
  const rm = (i) => setLinks(l => l.filter((_, idx) => idx !== i));

  const save = async () => {
    const cleaned = links
      .map(l => ({ nome: (l.nome || "").trim(), url: (l.url || "").trim() }))
      .filter(l => l.nome && l.url);
    setSaving(true);
    try {
      await api.put(`/marketplace/books/${book.id}`, { marketplace_links: cleaned });
      toast.success("Links salvos");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!book} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Links de Marketplaces — {book.titulo}</DialogTitle>
          <DialogDescription>
            Cadastre onde o livro está disponível para venda. O autor verá esses links no portal dele e poderá copiar ou abrir cada um.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {links.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 p-2 border border-border rounded-sm bg-muted/30">
              <div className="col-span-4">
                <Label className="text-[10px] text-muted-foreground">Marketplace</Label>
                <Input
                  data-testid={`link-nome-${i}`}
                  placeholder="Ex.: Amazon"
                  value={l.nome}
                  onChange={(e) => update(i, "nome", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="col-span-7">
                <Label className="text-[10px] text-muted-foreground">URL</Label>
                <Input
                  data-testid={`link-url-${i}`}
                  placeholder="https://…"
                  value={l.url}
                  onChange={(e) => update(i, "url", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="col-span-1 flex items-end justify-end">
                <Button size="sm" variant="ghost" onClick={() => rm(i)} data-testid={`link-rm-${i}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          {links.length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-4">
              Nenhum link cadastrado. Use o botão abaixo para adicionar.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={add} data-testid="link-add">
            <Plus className="w-4 h-4 mr-1" />Adicionar link
          </Button>
          <Button onClick={save} disabled={saving} data-testid="link-save">
            <Save className="w-4 h-4 mr-1" />Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalesSection({ author, summary, onChange }) {
  const books = summary?.books || [];
  const sales = summary?.sales || [];
  const [showNew, setShowNew] = useState(false);
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ book_id: "", marketplace: "Amazon", valor_venda: 0, data_venda: todayISO() });

  const save = async () => {
    if (!form.book_id) return toast.error("Selecione o livro");
    if (!form.valor_venda || Number(form.valor_venda) <= 0) return toast.error("Informe o valor");
    try {
      await api.post("/marketplace/sales", { ...form, valor_venda: Number(form.valor_venda) });
      toast.success("Venda registrada");
      setShowNew(false); onChange();
      setForm({ book_id: "", marketplace: "Amazon", valor_venda: 0, data_venda: todayISO() });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const del = async (s) => {
    if (!window.confirm("Excluir esta venda?")) return;
    try { await api.delete(`/marketplace/sales/${s.id}`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const bookName = (id) => books.find(b => b.id === id)?.titulo || "—";

  const statusBadge = (s) => {
    const map = {
      livre: { c: "text-emerald-700 border-emerald-200", l: "Disponível" },
      solicitado: { c: "text-amber-700 border-amber-200", l: "Solicitado" },
      pago: { c: "text-blue-700 border-blue-200", l: "Pago" },
    };
    const m = map[s?.royalty_status] || map.livre;
    return <Badge variant="outline" className={`text-[9px] ${m.c}`}>{m.l}</Badge>;
  };

  return (
    <Card className="rounded-sm overflow-hidden">
      <div className="p-3 border-b border-border flex justify-between items-center">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Vendas em marketplaces</div>
        <Button data-testid="sale-new-btn" size="sm" onClick={() => setShowNew(true)} disabled={books.length === 0}><Plus className="w-3.5 h-3.5 mr-1" />Nova venda</Button>
      </div>
      <Table>
        <TableHeader className="bg-secondary/60"><TableRow>
          <TableHead>Livro</TableHead><TableHead>Marketplace</TableHead><TableHead>Data</TableHead>
          <TableHead className="text-right">Valor</TableHead><TableHead className="text-right">Royalty</TableHead>
          <TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {sales.map(s => (
            <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
              <TableCell className="font-medium">{bookName(s.book_id)}</TableCell>
              <TableCell>{s.marketplace}</TableCell>
              <TableCell>{fmtDate(s.data_venda)}</TableCell>
              <TableCell className="text-right">{fmtBRL(s.valor_venda)}</TableCell>
              <TableCell className="text-right text-[hsl(var(--primary))] font-semibold">{fmtBRL(s.royalty_value)}</TableCell>
              <TableCell>{statusBadge(s)}</TableCell>
              <TableCell className="text-right">
                {s.royalty_status === "livre" && (
                  <Button size="sm" variant="ghost" onClick={() => del(s)} data-testid={`sale-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {sales.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Nenhuma venda lançada.</TableCell></TableRow>}
        </TableBody>
      </Table>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova venda em marketplace</DialogTitle>
            <DialogDescription>O royalty de 15% é calculado automaticamente sobre o valor de venda.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Livro</Label>
              <Select value={form.book_id} onValueChange={(v) => setForm(f => ({ ...f, book_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="sale-book"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{books.map(b => <SelectItem key={b.id} value={b.id}>{b.titulo}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold">Marketplace</Label>
              <Select value={form.marketplace} onValueChange={(v) => setForm(f => ({ ...f, marketplace: v }))}>
                <SelectTrigger className="mt-1" data-testid="sale-marketplace"><SelectValue /></SelectTrigger>
                <SelectContent>{MARKETPLACES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Valor (R$)</Label><Input data-testid="sale-valor" type="number" step="0.01" value={form.valor_venda} onChange={(e) => setForm(f => ({ ...f, valor_venda: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs font-semibold">Data</Label><Input data-testid="sale-data" type="date" value={form.data_venda} onChange={(e) => setForm(f => ({ ...f, data_venda: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="text-xs text-muted-foreground">Royalty calculado: <b className="text-[hsl(var(--primary))]">{fmtBRL((Number(form.valor_venda) || 0) * 0.15)}</b> (15%)</div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button><Button onClick={save} data-testid="sale-save"><Save className="w-3.5 h-3.5 mr-1" />Registrar venda</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RoyaltiesSection({ summary }) {
  const requests = summary?.requests || [];
  return (
    <Card className="rounded-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-secondary/60"><TableRow>
          <TableHead>Solicitada em</TableHead><TableHead className="text-right">Valor</TableHead>
          <TableHead>Banco / PIX</TableHead><TableHead>Previsão</TableHead><TableHead>Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {requests.map(r => (
            <TableRow key={r.id} data-testid={`req-row-${r.id}`}>
              <TableCell className="text-xs">{fmtDate(r.requested_at?.slice(0, 10))}</TableCell>
              <TableCell className="text-right font-semibold">{fmtBRL(r.total_valor)}</TableCell>
              <TableCell className="text-xs">{r.banco} {r.agencia && `· Ag ${r.agencia}`} {r.conta && `· Cc ${r.conta}`} {r.pix_chave && `· PIX ${r.pix_chave}`}</TableCell>
              <TableCell className="text-xs">{fmtDate(r.prev_pagamento)}</TableCell>
              <TableCell>
                <Badge variant="outline" className={`text-[10px] ${r.status === "pago" ? "text-emerald-700 border-emerald-200" : "text-amber-700 border-amber-200"}`}>
                  {r.status === "pago" ? "Pago" : "Pendente"}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
          {requests.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nenhuma solicitação ainda.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function LoginDialog({ open, onClose, author, editing, onSaved }) {
  const [form, setForm] = useState({ username: "", password: "", active: true });
  useEffect(() => {
    if (!open || !author) return;
    if (editing && author.user) setForm({ username: author.user.username, password: "", active: !!author.user.active });
    else {
      const base = (author.nome || "").toLowerCase().split(" ")[0].replace(/[^a-z0-9]/g, "");
      setForm({ username: base, password: "", active: true });
    }
  }, [open, author, editing]);

  if (!author) return null;
  const save = async () => {
    try {
      if (editing && author.user) {
        const payload = { username: form.username, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/authors/${author.client_id}/login`, payload);
        toast.success("Login atualizado");
      } else {
        if (!form.password) return toast.error("Defina uma senha");
        await api.post("/authors/login", { client_id: author.client_id, username: form.username, password: form.password });
        toast.success("Login criado");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const del = async () => {
    if (!window.confirm("Excluir o login deste autor? Ele perderá acesso ao portal.")) return;
    try { await api.delete(`/authors/${author.client_id}/login`); toast.success("Login removido"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Editar login" : "Criar login"} — {author.nome}</DialogTitle>
          <DialogDescription>O autor usará usuário e senha para acessar o Portal do Autor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold">Usuário</Label><Input data-testid="login-username" value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))} className="mt-1" /></div>
          <div><Label className="text-xs font-semibold">{editing ? "Nova senha (opcional)" : "Senha"}</Label><Input data-testid="login-password" type="text" placeholder={editing ? "Deixe em branco para manter" : "min. 4 caracteres"} value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} className="mt-1" /></div>
          {editing && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="lact" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} />
              <Label htmlFor="lact" className="text-xs">Login ativo</Label>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="ghost" onClick={del} className="text-red-600 mr-auto"><Trash2 className="w-3.5 h-3.5 mr-1" />Excluir</Button>}
          <Button variant="ghost" onClick={onClose}><X className="w-3.5 h-3.5 mr-1" />Cancelar</Button>
          <Button onClick={save} data-testid="login-save"><Save className="w-3.5 h-3.5 mr-1" />Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
